// Basic
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
// Libs
import { useTranslation } from 'react-i18next';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import useSidebarActiveButtonStore from '@/app/store/sidebarActiveButton';
import useColorsStore from '@/app/store/colors';
import useSessionStore from '@/app/store/session';
// Components
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar
} from '@/common-utils/components/ui/sidebar';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import SidebarUserFooter from './components/SidebarUserFooter';
import SidebarCollapsibleMenu from './components/SidebarCollapsibleMenu';
import SidebarComplexHeader from './components/SidebarComplexHeader';
import { Separator } from '../../ui/separator';
// Common
import SIDEBAR_STRUCTURE from '@/app/root/enums/SidebarStructure';
import compareSimilarUrls from '@/common-utils/functions/compareSimilarUrls';
import RouteUrls from '@/app/root/enums/RouteUrls';
// Style
import './Sidebar.scss';

/**
 * @returns {JSX} AppSidebar
 */
export function AppSidebar() {

    // Store
    const { activeButton, setActiveButton } = useSidebarActiveButtonStore();
    const { dynamicBackgroundColor, dynamicTextColor, isLight } = useColorsStore();
    const session = useSessionStore(s => s.session);
    const permissions = session?.permissions;

    // State
    const [clickedButton, setClickedButton] = useState(undefined);
    const [showBtnPreviousUrl, setShowBtnPreviousUrl] = useState(false);

    // Hooks
    const { setOpen, setOpenMobile, isMobile, open: isOpenSidebar } = useSidebar();
    const { t: translate } = useTranslation();
    const location = useLocation();

    // Functions
    // Check if the route has permissions
    const hasRoutePermissions = (route) => !route?.hasPermissions || route.hasPermissions(permissions);
    // Get the visible groups
    const visibleGroups = useMemo(() => (
        Object.keys(SIDEBAR_STRUCTURE).reduce((groups, groupKey) => {
            const group = SIDEBAR_STRUCTURE[groupKey];
            const subGroups = group.groupItems
                .map((subGroup) => ({
                    ...subGroup,
                    items: (subGroup.items ?? []).filter((item) => hasRoutePermissions(item))
                }))
                .filter((subgr) => subgr.items.length > 0);

            if (subGroups.length > 0) {
                groups.push({ groupKey, group, subGroups });
            }
            return groups;
        }, [])
    ), [permissions]);

    const getLinkClassName = (item) => {
        const hoverClass = isLight ? 'hover-light' : 'hover-dark';
        const activeClass = activeButton === item.id ? (isLight ? 'is-active-light' : 'is-active-dark') : '';
        return cn(hoverClass, item.id, activeClass);
    };

    // Hooks
    useEffect(() => {
        Object.values(RouteUrls).forEach((route) => {
            if (location.pathname !== '/' && compareSimilarUrls(location.pathname, route.url)) {
                setShowBtnPreviousUrl(!!route.previousBtn);
            }
        });
    }, [location]);

    // Render
    return (
        <Sidebar
            collapsible='icon'
            className={'shadow-right sidebar'}
            style={{ backgroundColor: dynamicBackgroundColor, color: dynamicTextColor }}
        >
            <SidebarComplexHeader
                dynamicTextColor={dynamicTextColor}
                isLight={isLight}
                activeButton={activeButton}
                setActiveButton={setActiveButton}
                showBtnPreviousUrl={showBtnPreviousUrl}
                setShowBtnPreviousUrl={setShowBtnPreviousUrl}
            />
            <SidebarContent
                className='[&::-webkit-scrollbar]:w-2
                            [&::-webkit-scrollbar-track]:rounded-full
                            [&::-webkit-scrollbar-thumb]:rounded-full'
                style={{ scrollbarColor: `${dynamicTextColor} transparent` }}
            >
                {!showBtnPreviousUrl && visibleGroups.map(({ groupKey, group, subGroups }) => (
                    <SidebarGroup key={groupKey}>
                        <SidebarGroupContent>
                            <SidebarGroupLabel>
                                <Entity entity={group.label} style={{ color: dynamicTextColor }} />
                            </SidebarGroupLabel>
                            <Separator style={{ backgroundColor: dynamicTextColor }} />
                            <SidebarMenu className='space-y-1 mt-1'>
                                {subGroups.map((subGroup) => {
                                    if (subGroup.items.length > 1) {
                                        return (
                                            <SidebarCollapsibleMenu
                                                key={subGroup.rootEntity ?? subGroup.id ?? subGroup.items[0].id}
                                                subGroup={subGroup}
                                                tooltip={translate(subGroup.rootEntity)}
                                                dynamicTextColor={dynamicTextColor}
                                                isLight={isLight}
                                                activeButton={activeButton}
                                                setActiveButton={setActiveButton}
                                                clickedButton={clickedButton}
                                                setClickedButton={setClickedButton}
                                            />
                                        );
                                    }
                                    const item = subGroup.items[0];
                                    return (
                                        <SidebarMenuItem key={item.id ?? item.entity} data-cy={item.id}>
                                            <SidebarMenuButton
                                                asChild
                                                tooltip={translate(item.entity)}
                                                className={cn(isOpenSidebar
                                                    ? 'justify-start px-2 gap-2'
                                                    : 'justify-center px-0 gap-0')}
                                            >
                                                <Link
                                                    to={item.url}
                                                    onClick={() => {
                                                        if (isMobile) {
                                                            setOpenMobile(false);
                                                        } else {
                                                            setOpen(false);
                                                        }
                                                        setActiveButton(item.id);
                                                    }}
                                                    className={getLinkClassName(item)}
                                                >
                                                    <Icon icon={item.icon} />
                                                    {isOpenSidebar &&
                                                        <Entity entity={item.entity} style={{ color: dynamicTextColor }} />}
                                                </Link>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    );
                                })}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                ))}
            </SidebarContent>
            <SidebarUserFooter
                dynamicTextColor={dynamicTextColor}
                isLight={isLight}
                activeButton={activeButton}
                setActiveButton={setActiveButton}
            />
        </Sidebar>
    );
}
